import { AppBlock } from '@boost/widget-integration-tae/lib';
import { Module, ModuleRefImpl } from 'framework/core';

import { DomHelper } from '../../helpers/dom.helper';
import { AppService } from '../app/app.service';
import { B2BModule } from '../b2b/b2b.module';
import { CustomizationCompatibility } from '../backward-compatibility/customization.compatibility';
import { CartModule } from '../cart/cart.module';
import { PlatformModule } from '../platform/platform.module';
import { RecentlyViewedProductModule } from '../recently-viewed-product/recently-viewed-product.module';
import { TemplateModule } from '../template/template.module';
import { RecommendationAnalytic } from './recommendation.analytic';
import { RecommendationAPI } from './recommendation.api';
import { RecommendationWidgetController } from './recommendation.controller';
import { RecommendationModel } from './recommendation.model';
import { RecommendationService } from './recommendation.service';

@Module({
  imports: [TemplateModule, RecentlyViewedProductModule, CartModule, PlatformModule, B2BModule],
  providers: [
    DomHelper,
    RecommendationAnalytic,
    RecommendationService,
    RecommendationAPI,
    CustomizationCompatibility,
    RecommendationWidgetController,
  ],
  exports: [RecommendationModel, RecommendationWidgetController],
})
export class RecommendationModule {
  constructor(
    protected moduleRef: ModuleRefImpl,
    protected appService: AppService
  ) {
    this.setupBlockListener();
  }

  /**
   * Sets up the TAE App block listener for recommendation widgets.
   * Override this method to customize block registration logic or support additional block types.
   *
   * @example
   * // Add support for custom block types
   * protected setupBlockListener() {
   *   this.appService.TAEApp.addRegistryBlockListener((block) => {
   *     if (block.blockType === 'recommendation' || block.blockType === 'vip-recommendation') {
   *       this.onInitBlock(block);
   *     }
   *   });
   * }
   */
  protected setupBlockListener(): void {
    this.appService.TAEApp.addRegistryBlockListener((block) => {
      if (block.blockType === 'recommendation') {
        this.onInitBlock(block);
      }
    });
  }

  /**
   * Called when a recommendation block is initialized.
   * Override this method to customize widget initialization or add shop-specific setup logic.
   *
   * @param block - The AppBlock instance being initialized
   * @returns Promise that resolves when the block initialization is complete
   *
   * @example
   * // Add shop-specific initialization
   * protected async onInitBlock(block: AppBlock) {
   *   // Add custom tracking before widget initialization
   *   this.trackWidgetInit(block.id);
   *   await super.onInitBlock(block);
   * }
   */
  protected async onInitBlock(block: AppBlock): Promise<void> {
    try {
      if (block.document instanceof HTMLElement) {
        const widget: RecommendationWidgetController = await this.moduleRef.resolve(
          RecommendationWidgetController
        );

        await this.connectWidget(widget, block);
      }
    } catch (error) {
      this.handleBlockInitError(error as Error, block);
    }
  }

  /**
   * Connects the widget controller to the DOM element.
   * Override this method to customize widget connection parameters or add pre-connection logic.
   *
   * @param widget - The widget controller instance
   * @param block - The AppBlock instance
   * @returns Promise that resolves when the widget connection is complete
   *
   * @example
   * // Add custom widget connection parameters
   * protected async connectWidget(widget, block) {
   *   const customParams = {
   *     widgetId: this.parseWidgetId(block.id),
   *     shopTheme: this.getShopTheme(),
   *   };
   *   widget.connect(customParams, block.document);
   * }
   */
  protected async connectWidget(
    widget: RecommendationWidgetController,
    block: AppBlock
  ): Promise<void> {
    widget.connect(
      {
        widgetId: this.parseWidgetId(block.id),
      },
      block.document as HTMLElement
    );
  }

  /**
   * Parses the widget ID from the block ID.
   * Override this method to customize widget ID extraction logic for different shops or naming conventions.
   *
   * @param blockId - The raw block ID from TAE
   * @returns The parsed widget ID
   *
   * @example
   * // Custom widget ID parsing for shop with different prefix
   * protected parseWidgetId(blockId: string): string {
   *   return blockId.replace('custom-widget-prefix-', '');
   * }
   */
  protected parseWidgetId(blockId: string): string {
    return blockId.replace('boost-sd-widget-', '');
  }

  /**
   * Handles errors during block initialization.
   * Override this method to customize error handling, reporting, or recovery logic.
   *
   * @remarks
   * The default implementation logs errors to the console. Technical Support teams
   * can extend this to integrate with shop-specific error tracking services
   * (e.g., Sentry, Bugsnag) or implement custom fallback behavior.
   *
   * @param error - The error that occurred during initialization
   * @param block - The AppBlock that failed to initialize
   *
   * @example
   * // Custom error reporting to shop's monitoring system
   * protected handleBlockInitError(error: Error, block: AppBlock): void {
   *   this.sendToShopMonitoring({
   *     type: 'widget_init_error',
   *     widgetId: block.id,
   *     error: error.message,
   *   });
   *   super.handleBlockInitError(error, block);
   * }
   */
  protected handleBlockInitError(error: Error, block: AppBlock): void {
    console.error(`Failed to initialize recommendation widget for block ${block.id}:`, error);
  }
}
